import {
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { useCollectionList } from '@/api/queries';
import type { CollectionKind } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { ListSearch } from '@/components/ListSearch';
import { ListFooter } from '@/components/PagedList';
import { PageHeader } from '@/components/PageHeader';
import { ErrorAlert, PageLoader } from '@/components/QueryState';
import { fromNow } from '@/lib/format';
import { useListUrlState } from '@/lib/useListUrlState';
import { COLLECTION_KINDS, kindLabel } from './collectionMeta';

export function CollectionsPage() {
  const url = useListUrlState();
  const showArchived = url.get('archived') === 'true';
  const list = useCollectionList({ q: url.q, archived: showArchived || undefined });
  const [open, modal] = useDisclosure();
  return (
    <>
      <PageHeader
        title="Collections"
        description="Editorial rows and the hero. Put them on the homepage under Home layouts."
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={modal.open}>
            New collection
          </Button>
        }
      />
      <Group mb="md" gap="md">
        <ListSearch
          label="Search collections"
          placeholder="Search titles"
          value={url.q}
          onSearch={url.setQ}
        />
        <Switch
          label="Show archived"
          checked={showArchived}
          onChange={(e) => url.setFilter('archived', e.currentTarget.checked ? 'true' : null)}
        />
      </Group>
      {list.isPending ? (
        <PageLoader />
      ) : list.error ? (
        <ErrorAlert error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          title={url.q ? 'No collections match' : 'No collections yet'}
          action={
            <Button variant="default" onClick={modal.open}>
              New collection
            </Button>
          }
        >
          {url.q
            ? 'Try another search.'
            : 'Collections group programmes into rows like “Animal friends”.'}
        </EmptyState>
      ) : (
        <>
          <Table.ScrollContainer minWidth={560}>
            <Table striped style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Kind</Table.Th>
                  <Table.Th>Programmes</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data.items.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/collections/${c.id}`} fw={600} c="inherit">
                        {c.title}
                      </Anchor>
                      {c.child_title && c.child_title !== c.title && (
                        <Text size="xs" c="dimmed">
                          Children see “{c.child_title}”
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>{kindLabel(c.kind)}</Table.Td>
                    <Table.Td>{c.items.length}</Table.Td>
                    <Table.Td>
                      {c.archived ? (
                        <Badge color="gray">Archived</Badge>
                      ) : (
                        <Badge color="leaf">Active</Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{fromNow(c.updated_at)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <ListFooter query={list} noun="collections" />
        </>
      )}
      <CreateCollectionModal opened={open} onClose={modal.close} />
    </>
  );
}

function CreateCollectionModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const form = useForm({
    initialValues: { title: '', kind: 'editorial_row' as CollectionKind },
    validate: { title: (v) => (v.trim() ? null : 'Add a title.') },
  });
  const create = useMutation({
    mutationFn: (v: typeof form.values) =>
      adminApi.createCollection({ title: v.title.trim(), kind: v.kind }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['collections'] });
      form.reset();
      onClose();
      navigate(`/collections/${c.id}`);
    },
  });
  return (
    <Modal opened={opened} onClose={onClose} title="New collection">
      <form onSubmit={form.onSubmit((v) => create.mutate(v))}>
        <Stack>
          <TextInput label="Title" required data-autofocus {...form.getInputProps('title')} />
          <Select
            label="Kind"
            allowDeselect={false}
            data={COLLECTION_KINDS.map((k) => ({
              value: k.value,
              label: `${k.label} — ${k.description}`,
            }))}
            {...form.getInputProps('kind')}
          />
          {create.error && (
            <Text size="sm" c="coral.8" role="alert">
              {errorMessage(create.error)}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
